import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { tokenManager } from './tokenManager';

/**
 * Centralized API client utility
 * Provides configured axios instances and helper methods
 * Includes automatic 401 handling with token refresh and retry
 */

// Service base URLs from environment
export const JMAP_GATEWAY_URL = (import.meta as any).env?.VITE_JMAP_GATEWAY_URL || 'http://localhost:8082';
export const TENANT_SERVICE_URL = (import.meta as any).env?.VITE_TENANT_SERVICE_URL || 'http://localhost:8081';

// Track requests that are waiting for token refresh
interface PendingRequest {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

let isRefreshing = false;
let pendingRequests: PendingRequest[] = [];

/**
 * Process pending requests after token refresh
 */
function processPendingRequests(token: string | null, error?: Error): void {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error || new Error('Token refresh failed'));
    } else {
      resolve(token);
    }
  });
  pendingRequests = [];
}

/**
 * Create an axios instance with authentication and 401 handling
 */
function createAuthenticatedInstance(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add current token to every request
  instance.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const token = tokenManager.getToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  // Response interceptor - handle 401 errors
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      // Check if it's a 401 and we haven't already retried
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        // If already refreshing, wait for that to complete
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            pendingRequests.push({ resolve, reject });
          }).then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return instance.request(originalRequest);
          });
        }

        isRefreshing = true;

        try {
          const newToken = await tokenManager.handleUnauthorized();

          if (newToken) {
            // Retry the original request with new token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            processPendingRequests(newToken);
            return instance.request(originalRequest);
          } else {
            // Refresh failed - reject all pending requests
            const authError = new Error('Session expired. Please login again.');
            processPendingRequests(null, authError);
            return Promise.reject(authError);
          }
        } catch (refreshError) {
          const authError = refreshError instanceof Error ? refreshError : new Error('Token refresh failed');
          processPendingRequests(null, authError);
          return Promise.reject(authError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

// Shared axios instances with interceptors
const jmapGatewayInstance = createAuthenticatedInstance(JMAP_GATEWAY_URL);
const tenantServiceInstance = createAuthenticatedInstance(TENANT_SERVICE_URL);

/**
 * Generic API client for authenticated requests
 * Uses shared axios instance with 401 handling
 */
export class ApiClient {
  private instance: AxiosInstance;

  constructor(baseURL: string) {
    // Use shared instances for known URLs, create new for others
    if (baseURL === JMAP_GATEWAY_URL) {
      this.instance = jmapGatewayInstance;
    } else if (baseURL === TENANT_SERVICE_URL) {
      this.instance = tenantServiceInstance;
    } else {
      this.instance = createAuthenticatedInstance(baseURL);
    }
  }

  /**
   * GET request (token parameter kept for backward compatibility but not used)
   */
  async get<T>(endpoint: string, _token?: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(endpoint, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, _token?: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(endpoint, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, _token?: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(endpoint, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, _token?: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.patch(endpoint, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = void>(endpoint: string, _token?: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(endpoint, config);
    return response.data;
  }

  /**
   * GET request with 404 fallback to null (common pattern)
   */
  async getOrNull<T>(endpoint: string, _token?: string, config?: AxiosRequestConfig): Promise<T | null> {
    try {
      return await this.get<T>(endpoint, _token, config);
    } catch (err: any) {
      if (err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }
}

/**
 * Pre-configured API clients for common services
 */
export const jmapGatewayClient = new ApiClient(JMAP_GATEWAY_URL);
export const tenantServiceClient = new ApiClient(TENANT_SERVICE_URL);

/**
 * Legacy compatibility - create headers object
 * Use ApiClient methods instead for new code
 */
export function createAuthHeaders(token: string) {
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Get the shared axios instance for direct use (if needed)
 */
export function getJmapGatewayInstance(): AxiosInstance {
  return jmapGatewayInstance;
}
